import React, { useEffect } from "react";
import PropTypes from "prop-types";
import TitlePages from "../../layout/TitlePages";
import Breadcrumbs from "../../layout/Breadcrumbs";
import "./Integracion.css";

const TabButton = ({ id, target, label, active }) => (
  <button
    className={`btn btn-ite ${active ? "active" : ""}`}
    id={id}
    data-bs-toggle="pill"
    data-bs-target={target}
    type="button"
    role="tab"
    aria-controls={target.slice(1)}
    aria-selected={active}
  >
    {label}
  </button>
);

const GridItem = ({ href, imgSrc, imgSrcSm, fallbackSrc, altText, title, name, index }) => {
  const isLink = Boolean(href && href.trim() !== "");
  const CardElement = isLink ? "a" : "div";
  const linkAttributes = isLink
    ? {
        href,
        target: "_blank",
        rel: "noopener noreferrer",
        "aria-label": `${name} - ${title} (Ver síntesis curricular)`,
      }
    : {};

  // Prioridad de carga para las tarjetas de la primera fila en desktop (índice 0 a 3)
  const isPriority = index < 4;

  const handleImageError = (e) => {
    if (fallbackSrc && !e.currentTarget.dataset.triedFallback) {
      e.currentTarget.dataset.triedFallback = "true";
      e.currentTarget.removeAttribute("srcset");
      e.currentTarget.src = fallbackSrc;
    }
  };

  return (
    <div className="integracion-card-wrapper">
      <CardElement
        {...linkAttributes}
        className={`integracion-card ${isLink ? "integracion-card--interactive" : ""}`}
      >
        <div className="integracion-card__img-container">
          <img
            src={imgSrc}
            srcSet={imgSrcSm ? `${imgSrcSm} 360w, ${imgSrc} 640w` : undefined}
            sizes="(max-width: 575px) 340px, (max-width: 991px) 380px, 320px"
            alt={altText || name}
            className="integracion-card__img"
            loading={isPriority ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={index === 0 ? "high" : "auto"}
            width="320"
            height="400"
            onError={handleImageError}
          />
        </div>
        <div className="integracion-card__info">
          <div className="integracion-card__name">{name}</div>
          <div className="integracion-card__role">{title}</div>
          {isLink && (
            <div className="integracion-card__link">
              <span className="integracion-card__link-text">Síntesis curricular</span>
              <span className="integracion-card__link-arrow" aria-hidden="true">↗</span>
            </div>
          )}
        </div>
      </CardElement>
    </div>
  );
};

const Integración = () => {
  useEffect(() => {
    document.title = "Integración";
  }, []);

  const publicUrl = process.env.PUBLIC_URL || "";

  const gridItems = [
    {
      href: "https://itetlax.org.mx/assets/pdf/CG/1.pdf",
      imgSrc: `${publicUrl}/images/integracion/webp/1.webp`,
      imgSrcSm: `${publicUrl}/images/integracion/webp/sm/1.webp`,
      fallbackSrc: "https://itetlax.org.mx/assets/img/intCG/1.webp",
      altText: "Emmanuel Ávila González",
      title: "Consejero Presidente",
      name: "Lic. Emmanuel Ávila González",
    },
    {
      href: "",
      imgSrc: `${publicUrl}/images/integracion/webp/2.webp`,
      imgSrcSm: `${publicUrl}/images/integracion/webp/sm/2.webp`,
      fallbackSrc: "https://itetlax.org.mx/assets/img/intCG/2.jpg",
      altText: "Mtra. Arely Meza Granados.",
      title: "Secretaria Ejecutiva",
      name: "Mtra. Arely Meza Granados.",
    },
    {
      href: "https://itetlax.org.mx/assets/pdf/CG/4.pdf",
      imgSrc: `${publicUrl}/images/integracion/webp/5.webp`,
      imgSrcSm: `${publicUrl}/images/integracion/webp/sm/5.webp`,
      fallbackSrc: "https://itetlax.org.mx/assets/img/intCG/5.png",
      altText: "Janet Cervante Ahuatzi",
      title: "Consejera Electoral",
      name: "Lic. Janet Cervantes Ahuatzi",
    },
    {
      href: "https://itetlax.org.mx/assets/pdf/CG/5.pdf",
      imgSrc: `${publicUrl}/images/integracion/webp/6.webp`,
      imgSrcSm: `${publicUrl}/images/integracion/webp/sm/6.webp`,
      fallbackSrc: "https://itetlax.org.mx/assets/img/intCG/6.png",
      altText: "Hermenegildo Neria Carreño",
      title: "Consejero Electoral",
      name: "Lic. Hermenegildo Neria Carreño",
    },
    {
      href: "https://itetlax.org.mx/assets/pdf/CG/6.pdf",
      imgSrc: `${publicUrl}/images/integracion/webp/7.webp`,
      imgSrcSm: `${publicUrl}/images/integracion/webp/sm/7.webp`,
      fallbackSrc: "https://itetlax.org.mx/assets/img/intCG/7.png",
      altText: "Yedith Martínez Pinillo",
      title: "Consejera Electoral",
      name: "Lic. Yedith Martínez Pinillo",
    },
    {
      href: "https://itetlax.org.mx/assets/pdf/CG/7.pdf",
      imgSrc: `${publicUrl}/images/integracion/webp/8.webp`,
      imgSrcSm: `${publicUrl}/images/integracion/webp/sm/8.webp`,
      fallbackSrc: "https://itetlax.org.mx/assets/img/intCG/8.png",
      altText: "Dra. Anakaren Monserrat Rojas Cuautle",
      title: "Consejera Electoral",
      name: "Dra. Anakaren Monserrat Rojas Cuautle",
    },
    {
      href: "https://itetlax.org.mx/assets/pdf/CG/9.pdf",
      imgSrc: `${publicUrl}/images/integracion/webp/9.webp`,
      imgSrcSm: `${publicUrl}/images/integracion/webp/sm/9.webp`,
      fallbackSrc: "https://itetlax.org.mx/assets/img/intCG/9.png",
      altText: "Mtra. Brenda Fernanda Barrón Rugerio",
      title: "Consejera Electoral",
      name: "Mtra. Brenda Fernanda Barrón Rugerio",
    },
  ];

  const partyRepresentatives = [
    {
      href: "https://www.pantlaxcala.org.mx/",
      imgSrc: "https://itetlax.org.mx/assets/img/intPP/pan.jpg",
      altText: "pan",
    },
    {
      href: "https://pritlaxcala.org.mx/",
      imgSrc: "https://itetlax.org.mx/assets/img/intPP/pri.png",
      altText: "pri",
    },
    {
      href: "http://pttlaxcala.com.mx/",
      imgSrc: "https://itetlax.org.mx/assets/img/intPP/ptt.jpg",
      altText: "pt",
    },
    {
      href: "https://partidoverdetlaxcala.org/",
      imgSrc: "https://itetlax.org.mx/assets/img/intPP/pvem.png",
      altText: "verde",
    },
    {
      href: "https://movimientociudadano.mx/tlaxcala",
      imgSrc: "https://itetlax.org.mx/assets/img/intPP/mc.png",
      altText: "movimiento ciudadano",
    },
    {
      href: "https://www.partidoalianzaciudadanatlax.com/",
      imgSrc: "https://itetlax.org.mx/assets/img/intPP/pac.jpg",
      altText: "pac",
    },
    {
      href: "https://www.natlaxcala.mx/",
      imgSrc: "https://itetlax.org.mx/assets/img/intPP/PNAT.jpg",
      altText: "nueva alianza",
    },
    {
      href: "https://morena.org/",
      imgSrc: "https://itetlax.org.mx/assets/img/intPP/Morena.jpg",
      altText: "morena",
    },
    {
      href: "https://www.redessocialesprogresistas.org/tlaxcala/",
      imgSrc: "https://itetlax.org.mx/assets/img/intPP/rspt.png",
      altText: "rsp",
    },
    {
      href: "https://www.fuerzapormexicotlx.org/",
      imgSrc: "https://itetlax.org.mx/assets/img/intPP/fxmt.png",
      altText: "fuerza x mexico",
    },
    {
      href: "https://www.prd.org.mx/index.php/2505-en-tlaxcala-existe-un-prd-animado-y-fortalecido-para-proyectar-las-mejores-candidaturas-jesus-zambrano-grijalva",
      imgSrc: "https://itetlax.org.mx/assets/img/intPP/prdt.png",
      altText: "prdt",
    },
  ];

  return (
    <>
      <Breadcrumbs path={[{ label: "Integración" }]} />
      <TitlePages title="Integración" subTitle="" />
      <div className="margin-bottom-2 integracion-section">
        {/* Encabezado de navegación superior centrado (ancho completo) */}
        <div className="integracion-header-nav">
          <div
            className="nav nav-pills justify-content-center integracion-nav-pills"
            id="v-pills-tab"
            role="tablist"
            aria-orientation="horizontal"
          >
            <TabButton
              id="v-pills-integracion-tab"
              target="#v-pills-integracion"
              label="Integración"
              active
            />
            <TabButton
              id="v-pills-pp-tab"
              target="#v-pills-pp"
              label="Representantes de los Partidos Políticos"
            />
          </div>
        </div>

        {/* Contenido en col-12 completo */}
        <div className="tab-content w-100" id="v-pills-tabContent">
          <div
            className="tab-pane fade show active"
            id="v-pills-integracion"
            role="tabpanel"
            aria-labelledby="v-pills-integracion-tab"
          >
            <div className="w-100 integracion-grid">
              {gridItems.map((item, index) => (
                <GridItem key={item.name} {...item} index={index} />
              ))}
            </div>
          </div>

          <div
            className="tab-pane fade"
            id="v-pills-pp"
            role="tabpanel"
            aria-labelledby="v-pills-pp-tab"
            tabIndex="0"
          >
            <div className="w-100 Grid__four">
              {partyRepresentatives.map((rep, index) => (
                <div key={index}>
                  <a href={rep.href} target="_blank" rel="noreferrer">
                    <img
                      src={rep.imgSrc}
                      className="img-fluid hover-ite"
                      alt={rep.altText}
                      loading="lazy"
                      decoding="async"
                    />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Integración;

TabButton.propTypes = {
  id: PropTypes.string.isRequired,
  target: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  active: PropTypes.bool,
};

GridItem.propTypes = {
  href: PropTypes.string.isRequired,
  imgSrc: PropTypes.string.isRequired,
  imgSrcSm: PropTypes.string,
  fallbackSrc: PropTypes.string,
  altText: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  index: PropTypes.number,
};
